import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Download, CheckCheck, XCircle, Receipt, Eye } from "lucide-react";
import { api, idr, pesanGalat, fmtNum } from "../../../lib/api";
import { formatTanggal, hariIniISO } from "../../../lib/tanggal";
import InputRupiah from "../../../components/InputRupiah";
import {
  MDR_BASE, BATCH_DIEKSPOR, BATCH_SUDAH, BATCH_DIBATALKAN, Modal, DialogAlasan, BatchPill, StatusPill,
  unduhDenganToast, kelasInput, kelasTombol, kelasTombolUtama, kelasTombolKecil,
} from "./shared";

/** Batch Export Claim per tab (brief C.25-27, keputusan C7/C14/C5).
 *
 *  Export Claim = batch BERNOMOR dari semua Siap Claim tab ini yang belum masuk
 *  batch dan tidak ditandai Perlu Review (server yang mengumpulkan — bukan
 *  centangan layar). Berkasnya bisa diunduh ulang kapan saja; "Tandai Sudah
 *  Diklaim" per batch mengunci selamanya. Uangnya lewat "Catat CN dari
 *  distributor" = mesin Credit Note yang sudah ada (kategori "Claim",
 *  D 1260 / K 4910) — Claim MDR sendiri tidak menjurnal apa pun. */

// CN yang masih hidup memblokir CN kedua di server (409) — tombolnya hanya
// muncul kalau CN sebelumnya sudah dibatalkan/dihapus di menu Credit Note.
const CN_MATI = ["dibatalkan", "dihapus"];

export default function PanelBatch({ tab, hak, versi, onBerubah }) {
  const [items, setItems] = useState([]);
  const [memuat, setMemuat] = useState(true);
  const [rincian, setRincian] = useState(null);
  const [batal, setBatal] = useState(null);
  const [cn, setCn] = useState(null);
  const [busy, setBusy] = useState(false);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await api.get(`${MDR_BASE}/batch`, { params: { tab: tab.kode } });
      setItems(r.data.items || []);
    } catch (e) { toast.error(pesanGalat(e, "Gagal memuat batch")); }
    finally { setMemuat(false); }
  }, [tab.kode]);
  useEffect(() => { muat(); }, [muat, versi]);

  const tandaiSudah = async (b) => {
    setBusy(true);
    try {
      const r = await api.post(`${MDR_BASE}/batch/${b.id}/tandai-sudah`);
      toast.success(r.data.message || "Batch ditandai Sudah Diklaim");
      muat(); onBerubah?.();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menandai batch")); }
    finally { setBusy(false); }
  };
  const kirimBatal = async (alasan) => {
    try {
      const r = await api.post(`${MDR_BASE}/batch/${batal.id}/batalkan`, { alasan });
      toast.success(r.data.message || "Batch dibatalkan");
      setBatal(null);
      muat(); onBerubah?.();
    } catch (e) { toast.error(pesanGalat(e, "Gagal membatalkan batch")); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-testid="mdr-batch-panel">
      <div className="flex items-center gap-2">
        <Package className="w-5 h-5 text-emerald-600" />
        <h2 className="font-display font-bold text-slate-900">Batch Export Claim — {tab.nama || tab.kode}</h2>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs min-w-[760px]">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-2 py-2">Nomor Batch</th>
              <th className="text-left px-2 py-2">Dibuat</th>
              <th className="text-right px-2 py-2">Unit</th>
              <th className="text-right px-2 py-2">Total Nominal</th>
              <th className="text-left px-2 py-2">Status</th>
              <th className="text-left px-2 py-2">Credit Note</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {memuat ? (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Memuat...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Belum ada batch di tab ini</td></tr>
            ) : items.map((b) => {
              const cnHidup = b.cn && !CN_MATI.includes(b.cn.status_cn);
              return (
                <tr key={b.id} data-testid={`mdr-batch-row-${b.id}`}>
                  <td className="px-2 py-1.5 font-mono font-semibold">{b.kode}</td>
                  <td className="px-2 py-1.5">{formatTanggal(b.created_at, { denganJam: true })}<br />
                    <span className="text-slate-400">{b.created_by_name}</span></td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {fmtNum(b.jumlah)}{b.jumlah_batal ? <span className="text-rose-600"> (−{b.jumlah_batal})</span> : null}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{idr(b.total_nominal)}</td>
                  <td className="px-2 py-1.5"><BatchPill status={b.status} /></td>
                  <td className="px-2 py-1.5">
                    {b.cn ? (
                      <span className={cnHidup ? "text-emerald-700" : "text-slate-400 line-through"}>
                        {b.cn.code} · {idr(b.cn.nilai)}
                      </span>
                    ) : "-"}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex flex-wrap justify-end gap-1">
                      <button type="button" className={kelasTombolKecil} onClick={() => setRincian(b)}
                              data-testid={`mdr-batch-rincian-${b.id}`}><Eye className="w-3.5 h-3.5" /> Rincian</button>
                      {b.status !== BATCH_DIBATALKAN && (
                        <button type="button" className={kelasTombolKecil} data-testid={`mdr-batch-unduh-${b.id}`}
                                onClick={() => unduhDenganToast(`${MDR_BASE}/batch/${b.id}/xlsx`, `claim-mdr-${b.kode}.xlsx`)}>
                          <Download className="w-3.5 h-3.5" /> Excel
                        </button>
                      )}
                      {hak.ubah && b.status === BATCH_DIEKSPOR && (
                        <>
                          <button type="button" disabled={busy} className={kelasTombolKecil} data-testid={`mdr-batch-sudah-${b.id}`}
                                  onClick={() => tandaiSudah(b)}>
                            <CheckCheck className="w-3.5 h-3.5" /> Tandai Sudah Diklaim
                          </button>
                          <button type="button" data-testid={`mdr-batch-batal-${b.id}`} onClick={() => setBatal(b)}
                                  className="h-8 px-2.5 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-xs font-medium inline-flex items-center gap-1">
                            <XCircle className="w-3.5 h-3.5" /> Batalkan
                          </button>
                        </>
                      )}
                      {hak.catatCn && b.status === BATCH_SUDAH && !cnHidup && (
                        <button type="button" className={kelasTombolKecil} data-testid={`mdr-batch-cn-${b.id}`}
                                onClick={() => setCn(b)}>
                          <Receipt className="w-3.5 h-3.5" /> Catat CN
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rincian && <DialogRincianBatch batch={rincian} onTutup={() => setRincian(null)} />}
      {batal && (
        <DialogAlasan judul={`Batalkan batch ${batal.kode}?`} bahaya labelYa="Batalkan Batch" testid="mdr-batch-batal-dialog"
                      keterangan="Klaim di batch ini kembali Siap Claim tanpa batch (mis. berkas salah kirim). Batch yang sudah Sudah Diklaim tidak bisa dibatalkan — ajukan batal per unit."
                      onYa={kirimBatal} onTutup={() => setBatal(null)} />
      )}
      {cn && <DialogCatatCn batch={cn} tab={tab} onTutup={() => setCn(null)} onSelesai={() => { muat(); onBerubah?.(); }} />}
    </section>
  );
}

function DialogRincianBatch({ batch, onTutup }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let batal = false;
    api.get(`${MDR_BASE}/batch/${batch.id}`).then((r) => { if (!batal) setData(r.data); })
      .catch((e) => { if (!batal) toast.error(pesanGalat(e, "Gagal memuat rincian batch")); });
    return () => { batal = true; };
  }, [batch.id]);
  return (
    <Modal judul={`Batch ${batch.kode}`} ikon={Package} onTutup={onTutup} lebar="max-w-5xl" testid="mdr-batch-detail"
           sub={`${fmtNum(batch.jumlah)} unit · ${idr(batch.total_nominal)}`}>
      {!data ? <p className="text-sm text-slate-400 py-6 text-center">Memuat...</p> : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs min-w-[820px]">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-2 py-2">Tgl Pembelian</th><th className="text-left px-2 py-2">Aktivasi</th>
                <th className="text-left px-2 py-2">IMEI / SN</th><th className="text-left px-2 py-2">Tipe</th>
                <th className="text-right px-2 py-2">Nominal</th><th className="text-left px-2 py-2">Leasing</th>
                <th className="text-left px-2 py-2">Konsumen</th><th className="text-left px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.klaim || []).map((k) => (
                <tr key={k.klaim_id} data-testid={`mdr-batch-klaim-${k.klaim_id}`}>
                  <td className="px-2 py-1.5">{formatTanggal(k.tanggal_pembelian)}</td>
                  <td className="px-2 py-1.5">{formatTanggal(k.tanggal_aktivasi)}</td>
                  <td className="px-2 py-1.5 font-mono">{k.imei}<br /><span className="text-slate-400">{k.sn}</span></td>
                  <td className="px-2 py-1.5">{k.tipe_produk}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{idr(k.nominal)}</td>
                  <td className="px-2 py-1.5">{k.leasing}{k.tenor ? ` · ${k.tenor} bln` : ""}</td>
                  <td className="px-2 py-1.5">{k.nama_konsumen}</td>
                  <td className="px-2 py-1.5"><StatusPill status={k.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function DialogCatatCn({ batch, tab, onTutup, onSelesai }) {
  const [form, setForm] = useState({
    nilai: 0, tanggal: hariIniISO(), supplier_nama: tab.nama_supplier || tab.nama || tab.kode, no_referensi: "", keterangan: "",
  });
  const [busy, setBusy] = useState(false);
  const kirim = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${MDR_BASE}/batch/${batch.id}/catat-cn`, {
        nilai: Number(form.nilai) || 0, tanggal: form.tanggal || null, supplier_nama: form.supplier_nama || null,
        no_referensi: form.no_referensi || null, keterangan: form.keterangan || "",
      });
      toast.success(r.data.message || "Credit Note tercatat");
      onSelesai?.();
      onTutup();
    } catch (e) { toast.error(pesanGalat(e, "Gagal mencatat Credit Note")); }
    finally { setBusy(false); }
  };
  return (
    <Modal judul={`Catat CN dari distributor — ${batch.kode}`} ikon={Receipt} onTutup={onTutup} lebar="max-w-lg" testid="mdr-cn"
           sub="Credit Note kategori Claim lewat modul Credit Note (D 1260 Piutang CN Supplier / K 4910). Nilainya sesuai persetujuan distributor.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Nilai CN *</span>
          <InputRupiah value={form.nilai} onChange={(n) => setForm({ ...form, nilai: n })} testid="mdr-cn-nilai"
                       className={`${kelasInput} w-full mt-1`} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Tanggal</span>
          <input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                 className={`${kelasInput} w-full mt-1`} data-testid="mdr-cn-tanggal" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-600">Distributor / Supplier</span>
          <input value={form.supplier_nama} onChange={(e) => setForm({ ...form, supplier_nama: e.target.value })}
                 className={`${kelasInput} w-full mt-1`} data-testid="mdr-cn-supplier" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-600">No. Referensi CN distributor</span>
          <input value={form.no_referensi} onChange={(e) => setForm({ ...form, no_referensi: e.target.value })}
                 className={`${kelasInput} w-full mt-1`} data-testid="mdr-cn-ref" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-600">Keterangan</span>
          <input value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                 className={`${kelasInput} w-full mt-1`} data-testid="mdr-cn-ket" />
        </label>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onTutup} className={kelasTombol} data-testid="mdr-cn-batal">Batal</button>
        <button type="button" onClick={kirim} disabled={busy || !(Number(form.nilai) > 0)} className={kelasTombolUtama}
                data-testid="mdr-cn-simpan">{busy ? "Menyimpan..." : "Catat Credit Note"}</button>
      </div>
    </Modal>
  );
}
